import type { Debug } from "@otto-ec/global-resources/debug";
import { toggle } from "@otto-ec/global-resources/toggle";
import { captureErrorWithPayload } from "utils/error";
import { createExpiryScheduler, type ExpiryScheduler } from "utils/expiry";
import { createDefaultOptions } from "utils/ky";
import type { ApmDefaultLabels } from "@otto-ec/global-resources/apm";
import ky, { type BeforeRetryState } from "@otto-ec/global-resources/ky";

export interface CallBackendOptions {
	url: URL;
	priority: RequestPriority;
	feature: string | null;
	team: string | null;
	initiator: string;
	logging: {
		logger: Debug;
		errorKey: string;
		errorLabels: ApmDefaultLabels;
	};
}

export async function callBackend(options: CallBackendOptions): Promise<Response> {
	let tries = 1;

	const {
		url,
		priority,
		feature,
		team,
		initiator,
		logging: { logger, errorKey, errorLabels },
	} = options;
	const urlSearchParams = url.searchParams.toString();

	try {
		const headers = buildFetchHeaders(feature, team, initiator);
		const defaultOptions = createDefaultOptions(logger, url.toString());
		const kyOptions = {
			...defaultOptions,
			headers,
			priority,
			hooks: {
				...defaultOptions.hooks,
				beforeRetry: [
					({ retryCount }: BeforeRetryState) => {
						tries = retryCount + 1;
					},
				],
			},
		};
		const response = await ky(url, kyOptions);

		if (!response.ok) {
			logger.error(`${errorLabels.module} API endpoint returned a non ok response!`, response.status);
			captureErrorWithPayload({
				message: "callBackend error",
				type: errorKey,
				labels: errorLabels,
				custom: { url_search_params: urlSearchParams, response_type: response.type, tries },
				payload: response.status,
			});
			return Promise.reject();
		}
		return response;
	} catch (error) {
		logger.error(`${errorLabels.module} API request returned an error!`, error);
		captureErrorWithPayload({
			message: "callBackend error",
			type: errorKey,
			labels: errorLabels,
			custom: { url_search_params: urlSearchParams, tries },
			payload: error,
		});
		return Promise.reject();
	}
}

/**
 *
 */
function buildFetchHeaders(xFeature: string | null, xTeam: string | null, initiator: string): HeadersInit {
	return {
		Accept: "text/html;charset=UTF-8",
		"X-Requested-With": "XMLHttpRequest",
		"X-feature": xFeature ?? "unset",
		"X-team": xTeam ?? "unset",
		"X-initiator": initiator,
	};
}

/**
 *
 *
 */
export function buildSearchParams(params: Record<string, string | null | undefined>): URLSearchParams {
	return Object.entries(params).reduce((urlSearchParams, [key, value]) => {
		if (value) {
			urlSearchParams.append(key, value);
		}
		return urlSearchParams;
	}, new URLSearchParams());
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export abstract class BenefitElement extends HTMLElement {
	/**
 *
 *
 */
	expiry: ExpiryScheduler = createExpiryScheduler();

	/**
 *
 *
 */
	private initialized = false;

	/**
 *
 *
 */
	connectedCallback(): void {
		if (this.initialized) {
			return;
		}
		this.initialized = true;
		void (async () => {
			await this.onInit?.();
			await this.hydrate();
		})();
	}

	/**
 *
 */
	disconnectedCallback(): void {
		this.expiry.cleanup();
	}

	/**
 *
 *
 */
	protected abstract hydrate(): Promise<void>;

	/**
 *
 *
 *
 *
 */
	protected onInit?(): Promise<void>;

	/**
 *
 *
 *
 *
 */
	async triggerReload(): Promise<void> {
		await this.hydrate();
	}

	/**
 *
 *
 *
 *
 *
 */
	protected triggerReloadAt(expirationDate: string): void {
		this.expiry.scheduler(expirationDate, () => void this.hydrate());
	}
}

/**
 *
 *
 */
export function defineElement(elementClass: CustomElementConstructor, name: string, toggleKey: string): void {
	if (customElements && customElements.get(name) === undefined && !toggle.get(toggleKey, false)) {
		customElements.define(name, elementClass);
	}
}
